import { Link } from 'react-router-dom';
import { useLocalizedPath } from '@/hooks/useI18n';
import { getByHandle } from '@/data/loader';
import { useTranslation } from '@/hooks/useTranslation';

interface PraiaCardsProps {
  /** Handles das praias para exibir (ex: ['praia-do-farol', 'praia-das-encantadas']) */
  handles: string[];
  /** Título opcional do bloco */
  title?: string;
}

/** Cards de praias para inserção em posts MDX — linkagem interna com vida */
export function PraiaCards({ handles, title }: PraiaCardsProps) {
  const localize = useLocalizedPath();
  const { t } = useTranslation();

  const praias = handles.map(h => getByHandle('praia', h)).filter(Boolean);

  if (praias.length === 0) return null;

  return (
    <div className="not-prose my-8 sm:my-10">
      {title && (
        <h3 className="font-display text-lg sm:text-xl text-warm-dark mb-4 tracking-wide">
          {title}
        </h3>
      )}
      <div className="grid sm:grid-cols-2 gap-4">
        {praias.map(p => (
          <Link
            key={p.handle}
            to={localize(`/praias/${p.handle}`)}
            className="group flex gap-4 bg-white border border-sand-200 rounded-xl overflow-hidden transition-all hover:-translate-y-0.5 hover:shadow-md hover:border-teal-light/30"
          >
            {/* Imagem */}
            <div className="relative w-28 sm:w-32 flex-shrink-0 overflow-hidden">
              <img
                src={p.image}
                alt={p.title}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                loading="lazy"
                decoding="async"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/10" />
            </div>

            {/* Conteúdo */}
            <div className="py-3 pr-4 flex flex-col justify-center min-w-0">
              <h4 className="font-display text-sm sm:text-base text-warm-dark group-hover:text-teal-deep transition-colors leading-tight tracking-wide">
                {p.title.toUpperCase()}
              </h4>
              <p className="font-body text-xs text-warm-gray mt-1 line-clamp-2">{p.subtitle}</p>

              {/* Tags rápidas */}
              <div className="flex flex-wrap gap-1.5 mt-2">
                {p.kidsFriendly && (
                  <span className="inline-flex items-center gap-1 font-body text-[10px] bg-teal-light/10 text-teal-deep px-2 py-0.5 rounded-full">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    {t('praiaCards.kidsFriendly')}
                  </span>
                )}
                {p.surfFriendly && (
                  <span className="inline-flex items-center gap-1 font-body text-[10px] bg-ocean-50 text-ocean-700 px-2 py-0.5 rounded-full">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                    Surf
                  </span>
                )}
                {p.snorkelFriendly && (
                  <span className="inline-flex items-center gap-1 font-body text-[10px] bg-coral/10 text-coral px-2 py-0.5 rounded-full">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    Snorkel
                  </span>
                )}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
